/**
 * 69.74 step 2 (DECISIONS 302, the owner: "where do helicopters come from? ... I couldn't find any at the base", then "Parked at
 * a helipad"; GameConfig.helipad): each side's helicopters parked on a helipad behind its base (the setup places it, the base
 * planner settles it with the rest), one to a spot, a row across its deck (HELI_PAD).
 * - **A call flies one off the pad:** every helicopter call — a person's (respawn, Fly there, fire support) or the commander's
 *   (troops, pickups, the hot rescue's lift) — takes the side's lowest ready spot; the flight takes off from it and lands back
 *   on it (core/dropship.ts, `padSpot`).
 * - **Back on its spot it turns round** — refuelled and rearmed — for HELI_PAD.turnaroundSeconds, then it is ready again
 *   (`dropships.helis` counts those ready). A call no longer spends a helicopter; a helicopter lost is gone (its spot `out`
 *   with no flight to come back).
 * - **A pad destroyed grounds its own:** none ready, none turns round, and a call is refused *"Helipad destroyed"*.
 * Pure and deterministic; with no pad (the switch off, no helicopters, or no board base) nothing here runs.
 */
import type { DropshipFlight, GameConfig, GameState, HeliPad, HeliSpot, StructureState, Vec3 } from '../types';
import { HELI_PAD } from '../constants/helicopter';
import { PHYS_DT } from '../constants';
import { SHIP_CRASH } from '../constants/action';
import { countOf } from './defaultOn';
import { structureFrame, yawFacing } from './hits';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dcos, dsin } from './dmath';
import { vecAdd, vecNormalize, vecScale } from '../math';

type Dropships = NonNullable<GameState['dropships']>;

/** The helipads are on: the switch, and helicopters held. */
export const helipadOn = (config: Pick<GameConfig, 'helipad' | 'helicopters' | 'clock'>): boolean => !!config.helipad && countOf(config, 'helicopters') > 0;

/** Side `team`'s pad record and its structure (standing or not); null with none. */
export function padOf(state: Pick<GameState, 'dropships' | 'structures'>, team: number): { pad: HeliPad; s: StructureState } | null {
  const pad = state.dropships?.pads?.[team];
  const s = pad ? state.structures.find(x => x.id === pad.structureId) : undefined;
  return pad && s ? { pad, s } : null;
}

/** Where spot `k` of `n` lies on pad `s` (a unit direction): a row across its deck (its x), `spotPeople` apart, centred. */
export function padSpotDir(state: Pick<GameState, 'planets' | 'config'>, s: Pick<StructureState, 'planetIndex' | 'surfaceDir' | 'yaw'>, k: number, n: number): Vec3 {
  const up = vecNormalize(s.surfaceDir), { tx } = structureFrame(s, up);
  const th = ((k - (n - 1) / 2) * HELI_PAD.spotPeople * PERSON_BODY_HEIGHT * personSetOf(state).k) / state.planets[s.planetIndex].radius;
  return vecNormalize(vecAdd(vecScale(up, dcos(th)), vecScale(tx, dsin(th))));
}

/** The state with each side's pad record: a side holding helicopters and a helipad gets a spot for each, all ready. */
export function withHelipads(state: GameState): GameState {
  const a = state.dropships;
  if (!helipadOn(state.config) || !a?.helis) return state;
  const pads: Record<number, HeliPad> = {};
  for (const [t, n] of Object.entries(a.helis)) {
    const team = Number(t);
    const s = state.structures.filter(x => x.type === 'helipad' && state.players.find(p => p.id === x.ownerId)?.teamIndex === team).sort((x, y) => x.id - y.id)[0];
    if (s && n > 0) pads[team] = { structureId: s.id, spots: Array.from({ length: n }, () => ({ state: 'ready' as const })) };
  }
  return Object.keys(pads).length ? { ...state, dropships: { ...a, pads } } : state;
}

/** The side's next helicopter off its pad: its spot and where it stands; refused with the pad down or none ready; null with no pad (a call spends a helicopter, as before). */
export function padLaunch(state: Pick<GameState, 'dropships' | 'structures' | 'planets' | 'config'>, team: number): { spot: number; dir: Vec3 } | { refused: string } | null {
  const p = padOf(state, team);
  if (!p) return null;
  if (p.s.health <= 0) return { refused: 'Helipad destroyed' };
  const k = p.pad.spots.findIndex(x => x.state === 'ready');
  if (k < 0) return { refused: 'No helicopters ready' };
  return { spot: k, dir: padSpotDir(state, p.s, k, p.pad.spots.length) };
}

/** The ships' block with spot `spot` of `team`'s pad out on flight `flight`. */
export function padOut(a: Dropships, team: number, spot: number, flight: number): Dropships {
  const pad = a.pads?.[team];
  if (!pad) return a;
  return { ...a, pads: { ...a.pads, [team]: { ...pad, spots: pad.spots.map((x, k) => (k === spot ? { state: 'out' as const, flight } : x)) } } };
}

/** Flight `f` back on its spot at `tick` (gone, not lost): it turns round there, ready HELI_PAD.turnaroundSeconds later — a pad down keeps it grounded. */
export function padReturn(state: GameState, f: DropshipFlight, tick: number): GameState {
  const a = state.dropships, pad = f.padSpot !== undefined ? a?.pads?.[f.team] : undefined;
  if (!a || !pad || pad.spots[f.padSpot!]?.flight !== f.id) return state;
  const spots = pad.spots.map((x, k) => (k === f.padSpot ? { state: 'turn' as const, readyTick: tick + Math.round(HELI_PAD.turnaroundSeconds / PHYS_DT) } : x));
  return { ...state, dropships: { ...a, pads: { ...a.pads, [f.team]: { ...pad, spots } } } };
}

/**
 * The pads' tick: a helicopter turned round is ready (one more to call). A pad down grounds its side's (none ready); 2c: the
 * tick it falls, those on it (ready or turning round) are lost with it — each its wreck on its spot, burning — and one back
 * later stands grounded on the ruin. The same state when nothing changes.
 */
export function stepHelipads(state: GameState, tick: number): GameState {
  if (!state.dropships?.pads) return state;
  let next = state;
  for (const [t, pad] of Object.entries(state.dropships.pads)) {
    const team = Number(t), s = state.structures.find(x => x.id === pad.structureId), down = !s || s.health <= 0;
    if (down) {
      if (!pad.down) {
        for (let k = 0; k < pad.spots.length; k++) next = loseParked(next, team, k, tick);
        const a = next.dropships!, now = a.pads![team];
        next = { ...next, dropships: { ...a, pads: { ...a.pads, [team]: { ...now, down: true as const } } } };
      }
      if ((next.dropships!.helis?.[team] ?? 0) > 0) next = { ...next, dropships: { ...next.dropships!, helis: { ...next.dropships!.helis, [team]: 0 } } };
      continue;
    }
    if (!pad.spots.some(x => x.state === 'turn' && tick >= x.readyTick!)) continue;
    const a = next.dropships!, spots = pad.spots.map(x => (x.state === 'turn' && tick >= x.readyTick! ? readied(x) : x));
    next = { ...next, dropships: { ...a, pads: { ...a.pads, [team]: { ...pad, spots } }, helis: { ...a.helis, [team]: spots.filter(x => x.state === 'ready').length } } };
  }
  return next;
}

/** A spot turned round: ready, its body's wear kept (refuelled and rearmed, not repaired). */
function readied(x: HeliSpot): HeliSpot {
  return { state: 'ready', ...(x.health !== undefined ? { health: x.health } : {}), ...(x.parts ? { parts: x.parts } : {}) };
}

/** 69.74 step 2c: a parked helicopter's id as a body (spot `spot` of side `team`'s pad): −1000 − 64 team − spot (the host's view key too). */
export const PARKED_ID_BASE = -1000;
export const parkedId = (team: number, spot: number): number => PARKED_ID_BASE - 64 * team - spot;
/** The side and spot of a parked helicopter's id; null for any other id (a flight's, the wave ship's −1 − team). */
export function parkedOfId(id: number): { team: number; spot: number } | null {
  if (id > PARKED_ID_BASE || id <= PARKED_ID_BASE - 64 * 64) return null;
  const n = PARKED_ID_BASE - id;
  return { team: Math.floor(n / 64), spot: n % 64 };
}

/**
 * 69.74 step 2c: the helicopter parked on spot `spot` of side `team`'s pad lost at `tick` (shot or blasted there, or with its pad):
 * its spot lost, its wreck (look C's, on its belly: a `shipWreck`, cover, burning by its age) where it stood, the loss filed for
 * the Match's events, the side's ready count recounted. The same state when nothing stands there.
 */
export function loseParked(state: GameState, team: number, spot: number, tick: number): GameState {
  const a = state.dropships, p = padOf(state, team), x = p?.pad.spots[spot];
  if (!a || !p || !x || (x.state !== 'ready' && x.state !== 'turn')) return state;
  const dir = padSpotDir(state, p.s, spot, p.pad.spots.length), front = structureFrame(p.s, dir).tz;
  const id = state.structures.reduce((m, q) => Math.max(m, q.id), -1) + 1;
  const owner = state.players.filter(u => u.teamIndex === team).sort((u, v) => u.id - v.id)[0]?.id ?? -1;
  const wreck: StructureState = { id, type: 'shipWreck', planetIndex: p.s.planetIndex, surfaceDir: dir, health: SHIP_CRASH.wreckHealth, ownerId: owner, yaw: yawFacing(dir, front), wreck: { piece: 'heli', at: tick, shipId: parkedId(team, spot) } };
  const spots = p.pad.spots.map((y, k) => (k === spot ? { state: 'lost' as const } : y));
  const ready = p.s.health > 0 ? spots.filter(y => y.state === 'ready').length : 0;
  return {
    ...state, structures: [...state.structures, wreck],
    dropships: { ...a, pads: { ...a.pads, [team]: { ...p.pad, spots } }, helis: { ...a.helis, [team]: ready }, lost: [...(a.lost ?? []), { team, id: parkedId(team, spot), planetIndex: p.s.planetIndex, at: dir, tick, survived: [], lost: [] }] },
  };
}

/** One helicopter on its pad, as `parkedHelis` gives it. */
export interface ParkedHeli { team: number; spot: number; padId: number; planetIndex: number; dir: Vec3; facing: Vec3; turning: boolean; health?: number; parts?: number[] }

/**
 * 69.74 step 2b: the helicopters standing on the pads — each spot ready or turning round — where each stands and which way it
 * faces (the pad's front, toward the enemy's base): for the host to draw; 2c for the shots (a body), the eyes (the intel
 * record) and the seat. A spot out has its flight; one lost, its wreck. On a pad down only those back since stand (grounded).
 */
export function parkedHelis(state: Pick<GameState, 'dropships' | 'structures' | 'planets' | 'config'>): ParkedHeli[] {
  const out: ParkedHeli[] = [];
  for (const t of Object.keys(state.dropships?.pads ?? {})) {
    const p = padOf(state, Number(t));
    if (!p) continue;
    p.pad.spots.forEach((x, k) => {
      if (x.state !== 'ready' && x.state !== 'turn') return;
      const dir = padSpotDir(state, p.s, k, p.pad.spots.length);
      out.push({ team: Number(t), spot: k, padId: p.s.id, planetIndex: p.s.planetIndex, dir, facing: structureFrame(p.s, dir).tz, turning: x.state === 'turn', ...(x.health !== undefined ? { health: x.health } : {}), ...(x.parts ? { parts: x.parts } : {}) });
    });
  }
  return out;
}

/** Why the side cannot call a helicopter now, for the menus: its pad destroyed, else none ready (with a pad) or none left. */
export function helisRefusal(state: Pick<GameState, 'dropships' | 'structures'>, team: number): string {
  const p = padOf(state, team);
  if (!p) return 'No helicopters left';
  return p.s.health <= 0 ? 'Helipad destroyed' : 'No helicopters ready';
}
